import { useEffect, useRef, useState } from "react";
import { useIdioma } from "../lib/idioma";

/**
 * El recuadro donde el socio firma con el dedo.
 *
 * En un mostrador con tablet, firmar con el dedo es lo mismo que firmar el
 * papel, y ahorra la carpeta. Se dibuja en un lienzo y se guarda como imagen
 * junto a la versión del contrato y la fecha: las tres cosas juntas son la
 * firma; una sin las otras no prueba nada.
 *
 * Mientras no se confirma no se guarda nada. Una firma a medias, con el trazo
 * cortado porque alguien pasó de pantalla, no vale ni para el socio ni para el
 * club.
 */
export default function FirmaPad({
  alFirmar,
  firmada,
}: {
  alFirmar: (imagen: Blob | null) => void;
  firmada: boolean;
}) {
  const lienzo = useRef<HTMLCanvasElement>(null);
  const dibujando = useRef(false);
  const { t } = useIdioma();
  const [hayTrazo, setHayTrazo] = useState(false);

  // El lienzo se dimensiona a los pixeles de verdad de la pantalla: en un movil
  // de pantalla fina, si no, la firma sale como un garabato pixelado.
  useEffect(() => {
    const c = lienzo.current;
    if (!c) return;
    const caja = c.getBoundingClientRect();
    const escala = window.devicePixelRatio || 1;
    c.width = caja.width * escala;
    c.height = caja.height * escala;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.scale(escala, escala);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#16211b";
  }, []);

  const punto = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const caja = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - caja.left, y: e.clientY - caja.top };
  };

  function empezar(e: React.PointerEvent<HTMLCanvasElement>) {
    if (firmada) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const ctx = lienzo.current?.getContext("2d");
    if (!ctx) return;
    const { x, y } = punto(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    dibujando.current = true;
  }

  function seguir(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!dibujando.current) return;
    const ctx = lienzo.current?.getContext("2d");
    if (!ctx) return;
    const { x, y } = punto(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHayTrazo(true);
  }

  const soltar = () => {
    dibujando.current = false;
  };

  function borrar() {
    const c = lienzo.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    setHayTrazo(false);
    alFirmar(null);
  }

  function confirmar() {
    lienzo.current?.toBlob((imagen) => alFirmar(imagen), "image/png");
  }

  return (
    <div className="firma-pad">
      <canvas
        ref={lienzo}
        className={firmada ? "firmado" : undefined}
        onPointerDown={empezar}
        onPointerMove={seguir}
        onPointerUp={soltar}
        onPointerLeave={soltar}
      />
      <div className="fila separada">
        <span className="suave pequeno">
          {firmada ? t("fp.firmado") : hayTrazo ? t("fp.estaBien") : t("fp.firmaAqui")}
        </span>
        <div className="fila" style={{ gap: 4 }}>
          <button
            type="button"
            className="boton fantasma"
            onClick={borrar}
            disabled={!hayTrazo && !firmada}
            title={t("fp.repetirTitulo")}
          >
            ✕ {t("fp.repetir")}
          </button>
          <button
            type="button"
            className="boton secundario"
            onClick={confirmar}
            disabled={!hayTrazo || firmada}
          >
            ✓ {t("fp.confirmar")}
          </button>
        </div>
      </div>
    </div>
  );
}
